import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Loader2,
  Edit,
  Trash2,
  Globe,
  Mail,
  Phone,
  MapPin,
  User,
  FileText,
  Plus,
  ExternalLink,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { extCompanyService } from '../../../mantenimiento/services/extCompanyService.js';
import { ExtEmployeeModal } from './ExtEmployeeModal';
import { ExtContractModal } from './ExtContractModal';
import { useFactory } from '../../../../context/FactoryContext';

export const ExtCompanyDetails = ({ company, loading, onDelete, onEdit, readOnlyMode }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [isEmpModalOpen, setIsEmpModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);

  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [expandedContractIds, setExpandedContractIds] = useState([]);

  const queryClient = useQueryClient();

  const { selectedFactory, factories, factoryCodes } = useFactory();

  // Fetch employees when the employees tab is selected
  const { data: employeesResponse, isLoading: isLoadingEmployees } = useQuery({
    queryKey: ['ext-employees', company?.id],
    queryFn: () => extCompanyService.getExtEmployeeByExtCompany(company.id, 1, 100, false),
    enabled: !!company?.id && activeTab === 'employees',
  });

  const employees = employeesResponse?.data || [];

  // Fetch maintenance contracts when the contracts tab is selected
  const { data: contractsResponse, isLoading: isLoadingContracts } = useQuery({
    queryKey: ['ext-contracts', company?.id],
    queryFn: () => extCompanyService.getMaintenanceContracts({ company_id: company.id }, 1, 100),
    enabled: !!company?.id && activeTab === 'contracts',
  });

  const contracts = contractsResponse?.data || [];

  // Sort employees alphabetically by full name (A-Z)
  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim();
      const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim();
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
  }, [employees]);

  // Employee Mutations
  const createEmployeeMutation = useMutation({
    mutationFn: (empData) => extCompanyService.createExtEmployee(company.id, empData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ext-employees', company?.id] });
      setIsEmpModalOpen(false);
    },
    onError: (err) => {
      console.error(err);
      alert(err.message || 'Error adding employee');
    },
  });

  const updateEmployeeMutation = useMutation({
    mutationFn: (empData) => extCompanyService.updateExtEmployee(editingEmployee.id, empData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ext-employees', company?.id] });
      setIsEmpModalOpen(false);
      setEditingEmployee(null);
    },
    onError: (err) => {
      console.error(err);
      alert(err.message || 'Error updating employee');
    },
  });
  console.log(company?.id)

  const deleteEmployeeMutation = useMutation({
    mutationFn: (empId) => extCompanyService.deleteExtEmployee(empId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ext-employees', company?.id] });
    },
    onError: (err) => {
      console.error(err);
      alert(err.message || 'Error deleting employee');
    },
  });

  // Contract Mutations
  const createContractMutation = useMutation({
    mutationFn: (contractData) => extCompanyService.createMaintenanceContract(contractData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ext-contracts', company.id] });
      setIsContractModalOpen(false);
    },
    onError: (err) => {
      console.error(err);
    },
  });

  const updateContractMutation = useMutation({
    mutationFn: (contractData) => extCompanyService.updateMaintenanceContract(editingContract.id, contractData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ext-contracts', company.id] });
      setIsContractModalOpen(false);
      setEditingContract(null);
    },
    onError: (err) => {
      console.error(err);
    },
  });

  const deleteContractMutation = useMutation({
    mutationFn: (contractId) => extCompanyService.deleteMaintenanceContract(contractId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ext-contracts', company.id] });
    },
    onError: (err) => {
      console.error(err);
      alert(err.message || 'Error deleting maintenance contract');
    },
  });

  // Employee Handlers
  const handleAddEmployeeClick = () => {
    setEditingEmployee(null);
    setIsEmpModalOpen(true);
  };

  const handleEditEmployeeClick = (emp) => {
    setEditingEmployee(emp);
    setIsEmpModalOpen(true);
  };

  const handleDeleteEmployeeClick = (empId) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      deleteEmployeeMutation.mutate(empId);
    }
  };

  const handleSaveEmployee = async (formData) => {
    if (editingEmployee) {
      await updateEmployeeMutation.mutateAsync(formData);
    } else {
      await createEmployeeMutation.mutateAsync(formData);
    }
  };

  // Contract Handlers
  const handleAddContractClick = () => {
    setEditingContract(null);
    setIsContractModalOpen(true);
  };

  const handleEditContractClick = (contract) => {
    setEditingContract(contract);
    setIsContractModalOpen(true);
  };

  const handleDeleteContractClick = (contractId) => {
    if (window.confirm('Are you sure you want to delete this maintenance contract?')) {
      deleteContractMutation.mutate(contractId);
    }
  };

  const handleSaveContract = async (formData) => {
    if (editingContract) {
      await updateContractMutation.mutateAsync(formData);
    } else {
      await createContractMutation.mutateAsync(formData);
    }
  };

  const toggleExpandContract = (contractId) => {
    setExpandedContractIds((prev) =>
      prev.includes(contractId) ? prev.filter((id) => id !== contractId) : [...prev, contractId]
    );
  };

  // Date & Badge helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const checkIsExpiringSoon = (endDateStr) => {
    if (!endDateStr) return false;
    try {
      const end = new Date(endDateStr).getTime();
      const now = Date.now();
      const diffDays = (end - now) / (1000 * 60 * 60 * 24);
      return diffDays > 0 && diffDays <= 60;
    } catch {
      return false;
    }
  };

  const getContractTypeBadge = (type) => {
    switch (type) {
      case 'preventive':
        return { label: 'Preventive', className: 'bg-blue-900/50 text-blue-200 border border-blue-500/30' };
      case 'corrective':
        return { label: 'Corrective', className: 'bg-amber-900/50 text-amber-200 border border-amber-500/30' };
      case 'full_service':
        return { label: 'Full Service', className: 'bg-purple-900/50 text-purple-200 border border-purple-500/30' };
      default:
        return { label: type || 'Standard', className: 'bg-gray-700 text-gray-300' };
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center text-gray-400">
          <Loader2 className="animate-spin mb-4" size={32} />
          <p>Loading company details...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!company) {
    return (
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center text-gray-400">
          <Building2 className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Select a company to view its details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg flex flex-col max-h-auto relative">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-gray-400" />
          <h2 className="text-xl font-semibold text-white">
            Company Details
          </h2>
        </div>
        {!readOnlyMode && (
          <div className="flex items-center gap-2">
            {/* Edit Button only visible to higher-ups */}
            <button
              type="button"
              onClick={() => onEdit(company)}
              className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-blue-400 transition-colors"
              title="Edit company"
            >
              <Edit className="w-4 h-4" />
            </button>
            {/* Delete Button only visible to higher-ups*/}
            <button
              type="button"
              onClick={() => onDelete(company.id)}
              className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-red-400 transition-colors"
              title="Delete company"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="sticky top-0 z-10 px-6 border-b border-gray-700 bg-gray-800">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('details')}
            className={`px-4 py-3 flex-1 text-sm font-medium transition-colors hover:bg-gray-750 cursor-pointer relative ${activeTab === 'details' ? 'text-blue-400' : 'text-gray-400 hover:text-gray-200'
              }`}
          >
            Information
            {activeTab === 'details' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('employees')}
            className={`px-4 py-3 flex-1 text-sm font-medium transition-colors hover:bg-gray-750 cursor-pointer relative ${activeTab === 'employees' ? 'text-blue-400' : 'text-gray-400 hover:text-gray-200'
              }`}
          >
            Employees
            {activeTab === 'employees' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('contracts')}
            className={`px-4 py-3 flex-1 text-sm font-medium transition-colors hover:bg-gray-750 cursor-pointer relative ${activeTab === 'contracts' ? 'text-blue-400' : 'text-gray-400 hover:text-gray-200'
              }`}
          >
            Contracts
            {activeTab === 'contracts' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        {/* Information Tab */}
        {activeTab === 'details' ? (
          <div className={company?.factories !== undefined ? "space-y-2" : "space-y-6"}>
            {/* Basic Info Header Card */}
            <div className="flex items-center justify-between bg-gray-700/50 p-4 rounded-xl border border-gray-700 flex-wrap gap-2">
              <div>
                <h3 className="text-xl font-bold text-white mb-1">
                  {company.name}
                </h3>
                <p className="text-sm font-mono text-gray-400">
                  Tax ID: {company.tax_id || 'N/A'}
                </p>
              </div>
              <Badge className={company.active ? 'bg-green-900/50 text-green-200' : 'bg-red-900/50 text-red-200'}>
                {company.active ? 'Active Partner' : 'Inactive'}
              </Badge>
            </div>
            {company.factories !== undefined && (
              <div className="flex flex-wrap gap-2 ">
                {company?.factories?.map((factory_id) => (
                  <div key={factory_id}>
                    <Badge className="bg-blue-900/50 text-blue-200">
                      {factories?.find((factory) => factory.id === factory_id)?.name || factories.find((factory) => factory.id === factory_id)?.asset_code || "-"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Contact Person */}
              <div className="bg-gray-700/30 rounded-lg p-4 flex items-start gap-3">
                <User className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Contact Person</p>
                  <p className="text-white font-medium">{company.contact_name || 'N/A'}</p>
                </div>
              </div>

              {/* Phone */}
              <div className="bg-gray-700/30 rounded-lg p-4 flex items-start gap-3">
                <Phone className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Phone Number</p>
                  <p className="text-white font-medium">{company.phone || 'N/A'}</p>
                </div>
              </div>

              {/* Email */}
              <div className="bg-gray-700/30 rounded-lg p-4 flex items-start gap-3 md:col-span-2">
                <Mail className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-400 mb-0.5">Email Address</p>
                  {company.email ? (
                    <a
                      href={`mailto:${company.email}`}
                      className="text-blue-400 hover:underline text-sm font-medium truncate block"
                    >
                      {company.email}
                    </a>
                  ) : (
                    <p className="text-gray-300">N/A</p>
                  )}
                </div>
              </div>

              {/* Website */}
              <div className="bg-gray-700/30 rounded-lg p-4 flex items-start gap-3 md:col-span-2">
                <Globe className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-400 mb-0.5">Website</p>
                  {company.website ? (
                    <a
                      href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline text-sm font-medium truncate block"
                    >
                      {company.website}
                    </a>
                  ) : (
                    <p className="text-gray-300">N/A</p>
                  )}
                </div>
              </div>

              {/* Address */}
              <div className="bg-gray-700/30 rounded-lg p-4 flex items-start gap-3 md:col-span-2">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Physical Address</p>
                  <p className="text-white text-sm whitespace-pre-wrap">{company.address || 'N/A'}</p>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-gray-700/30 rounded-lg p-4 flex items-start gap-3 md:col-span-2">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <div className="w-full">
                  <p className="text-xs text-gray-400 mb-0.5">Notes & Remarks</p>
                  <p className="text-white text-sm whitespace-pre-wrap bg-gray-800/40 p-3 rounded-lg border border-gray-750 min-h-[80px]">
                    {company.notes || 'No notes added.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'employees' ? (
          //Employees Tab
          <div className="space-y-4">
            {/* Tab Header with Add Button only visible to higher-ups */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-white">Employees List</h3>
                <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full font-mono">
                  {employees.length}
                </span>
              </div>
              {!readOnlyMode && (<button
                type="button"
                onClick={handleAddEmployeeClick}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-600 bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Employee
              </button>)}
            </div>

            {/* List */}
            {isLoadingEmployees ? (
              <div className="flex justify-center py-8">
                <Loader2 className="animate-spin text-blue-500" size={24} />
              </div>
            ) : employees.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-gray-700 rounded-xl bg-gray-700/10">
                <User className="w-12 h-12 mx-auto mb-3 opacity-30 text-gray-400" />
                <p className="text-gray-400 text-sm">No employees registered for this company.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedEmployees.map((emp) => (
                  <div
                    key={emp.id}
                    className="bg-gray-700/30 border border-gray-700 rounded-xl p-4 flex justify-between items-center gap-4 hover:bg-gray-700/40 transition-all shadow-sm"
                  >
                    <div className="min-w-0">
                      <h4 className="text-white font-semibold text-sm truncate">
                        {emp.first_name} {emp.last_name}
                      </h4>
                      <p className="text-xs text-blue-400 font-medium truncate mt-0.5">{emp.position || 'No position specified'}</p>
                      <div className="mt-2 flex items-center gap-x-4 gap-y-1 text-xs text-gray-400 flex-wrap">
                        {emp.email && <span className="truncate">{emp.email}</span>}
                        {emp.phone && <span>{emp.phone}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={emp.active ? 'bg-green-900/50 text-green-200' : 'bg-red-900/50 text-red-200'}>
                        {emp.active ? 'Active' : 'Inactive'}
                      </Badge>
                      {!readOnlyMode && (<div>
                        <button
                          onClick={() => handleEditEmployeeClick(emp)}
                          className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-gray-700/50 rounded-lg transition-colors"
                          title="Edit employee"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteEmployeeClick(emp.id)}
                          className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-700/50 rounded-lg transition-colors"
                          title="Delete employee"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          //Contracts Tab
          <div className="space-y-4">
            {/* Tab Header with Add Button only visible to higher-ups*/}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-white">Maintenance Contracts</h3>
                <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full font-mono">
                  {contracts.length}
                </span>
              </div>
              {!readOnlyMode && (<button
                type="button"
                onClick={handleAddContractClick}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-600 bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Contract
              </button>
              )}
            </div>

            {/* List */}
            {isLoadingContracts ? (
              <div className="flex justify-center py-8">
                <Loader2 className="animate-spin text-blue-500" size={24} />
              </div>
            ) : contracts.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-gray-700 rounded-xl bg-gray-700/10">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30 text-gray-400" />
                <p className="text-gray-400 text-sm">No maintenance contracts registered for this company.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contracts.map((contract) => {
                  const isExpanded = expandedContractIds.includes(contract.id);
                  const contractTypeBadge = getContractTypeBadge(contract.contract_type);
                  const isExpiringSoon = checkIsExpiringSoon(contract.end_date);

                  return (
                    <div
                      key={contract.id}
                      className="bg-gray-700/30 border border-gray-700 rounded-xl p-4 transition-all shadow-sm hover:border-gray-600"
                    >
                      {/* Main Header Row */}
                      <div className="flex justify-between items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h4 className="text-white font-semibold text-base">
                              {contract.contract_number}
                            </h4>
                            <Badge className={contractTypeBadge.className}>
                              {contractTypeBadge.label}
                            </Badge>
                            <Badge className={contract.active ? 'bg-green-900/50 text-green-200' : 'bg-red-900/50 text-red-200'}>
                              {contract.active ? 'Active' : 'Inactive'}
                            </Badge>
                            {isExpiringSoon && (
                              <Badge className="bg-amber-900/50 text-amber-200 border border-amber-500/30">
                                Expiring Soon
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-blue-400 font-medium">
                            Service: <span className="capitalize">{contract.service_type?.replace(/_/g, ' ')}</span>
                          </p>
                        </div>

                        {/* Action Buttons */}
                        {!readOnlyMode && (
                          <div className="flex items-center gap-1.5 shrink-0">
                            {/*Upload document button (someday) */}
                            {/*contract.document_url && (
                            <a
                              href={contract.document_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-gray-700/50 rounded-lg transition-colors"
                              title="View Document"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}*/}
                            <button
                              onClick={() => handleEditContractClick(contract)}
                              className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-gray-700/50 rounded-lg transition-colors"
                              title="Edit Contract"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteContractClick(contract.id)}
                              className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-700/50 rounded-lg transition-colors"
                              title="Delete Contract"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Quick stats grid */}
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div className="bg-gray-800/60 p-2.5 rounded-lg border border-gray-750">
                          <span className="text-gray-400 block mb-0.5">Validity Period</span>
                          <span className="text-gray-200 font-medium">
                            {formatDate(contract.start_date)} - {formatDate(contract.end_date)}
                          </span>
                        </div>
                        <div className="bg-gray-800/60 p-2.5 rounded-lg border border-gray-750">
                          <span className="text-gray-400 block mb-0.5">Annual Cost</span>
                          <span className="text-emerald-400 font-semibold">
                            {contract.annual_cost !== undefined && contract.annual_cost !== null
                              ? `${Number(contract.annual_cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="bg-gray-800/60 p-2.5 rounded-lg border border-gray-750">
                          <span className="text-gray-400 block mb-0.5">SLA / Hours</span>
                          <span className="text-gray-200 font-medium">
                            {contract.response_time ? `${contract.response_time}h response` : 'N/A'} • {contract.service_hours || '24/7'}
                          </span>
                        </div>
                        <div className="bg-gray-800/60 p-2.5 rounded-lg border border-gray-750">
                          <span className="text-gray-400 block mb-0.5">Scope</span>
                          <span className="text-gray-200 font-medium truncate block">
                            {contract.asset_code ? `Asset: ${contract.asset_code}` : contract.factory_name ? `Factory: ${contract.factory_name}` : 'General / All'}
                          </span>
                        </div>
                      </div>

                      {/* Toggle extra details */}
                      {(contract.payment_terms || contract.included_services || contract.excluded_services) && (
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={() => toggleExpandContract(contract.id)}
                            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 transition-colors"
                          >
                            {isExpanded ? (
                              <>
                                <ChevronUp className="w-3.5 h-3.5" /> Hide Details
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-3.5 h-3.5" /> Show Full Details
                              </>
                            )}
                          </button>

                          {isExpanded && (
                            <div className="mt-2.5 pt-2.5 border-t border-gray-700/60 space-y-2 text-xs">
                              {contract.payment_terms && (
                                <div>
                                  <span className="text-gray-400 font-medium">Payment Terms:</span>{' '}
                                  <span className="text-gray-300">{contract.payment_terms}</span>
                                </div>
                              )}
                              {contract.included_services && (
                                <div>
                                  <span className="text-green-400 font-medium">Included Services:</span>
                                  <p className="text-gray-300 mt-0.5 whitespace-pre-wrap bg-gray-800/40 p-2 rounded border border-gray-750">
                                    {contract.included_services}
                                  </p>
                                </div>
                              )}
                              {contract.excluded_services && (
                                <div>
                                  <span className="text-red-400 font-medium">Excluded Services:</span>
                                  <p className="text-gray-300 mt-0.5 whitespace-pre-wrap bg-gray-800/40 p-2 rounded border border-gray-750">
                                    {contract.excluded_services}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Employee Modal */}
      {isEmpModalOpen && (
        <ExtEmployeeModal
          isOpen={isEmpModalOpen}
          onClose={() => {
            setIsEmpModalOpen(false);
            setEditingEmployee(null);
          }}
          onSave={handleSaveEmployee}
          employee={editingEmployee}
        />
      )}

      {/* Maintenance Contract Modal */}
      {isContractModalOpen && (
        <ExtContractModal
          isOpen={isContractModalOpen}
          onClose={() => {
            setIsContractModalOpen(false);
            setEditingContract(null);
          }}
          onSave={handleSaveContract}
          contract={editingContract}
          company={company}
        />
      )}
    </div>
  );
};

